import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import isEqual from 'react-fast-compare';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import EnzymeSelector from 'analyses/primers/EnzymeSelector';
import * as analytics from 'analytics/Analytics';
import PrimerBases, {isAssemblyPrimer} from 'assembly/PrimerBases';
import {assert, assertAndContinue} from 'Asserts';
import * as biojs from 'BioJS';
import {NucleotideType} from 'biojs/BioConstants';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {
  Collection as SequencePrimerCollection,
  Model as BioJSSequencePrimer,
  SearchPrimerCollection,
  SequencePrimerAttributes,
} from 'biojs/SequencePrimer';
import * as util from 'biojs/Util';
// @ts-expect-error
import ColorPicker from 'components/ColorPicker';
import {Sequence as ComponentSequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {MAX_PRIMER_NAME_LENGTH, PrimerPreferences} from 'constants/PrimerConstants';
import FolderDAO from 'file-browser/FolderDAO';
import FolderDAOCache from 'file-browser/FolderDAOCache';
// @ts-expect-error
import FolderSelectorView from 'file-browser/FolderSelectorView';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import Enzymes, {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import ComponentDNA from 'molbio/maps/selectables/DNA';
import {Selection} from 'molbio/maps/selectables/Selection';
import MeltingTempModal from 'molbio/melting-temp/MeltingTempModal';
import * as sequtil from 'molbio/SeqUtil';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import notifications from 'notifications';
import preferences from 'Preferences';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import * as RegistryUtil from 'registry/RegistryUtil';
import runModal from 'ui-library/runModal';
import * as checkPrimerUtil from 'util/CheckPrimer';
import * as TaskRunner from 'util/TaskRunner';

import AssemblyPrimerHelpTemplate from './AssemblyPrimerHelpTemplate';
import EditPrimerOptionsTemplate from './EditPrimerOptionsTemplate';
import EditPrimerPairTemplate from './EditPrimerPairTemplate';
import EditPrimerTabState, {EditPrimerTabStateAttributes, Secondary} from './EditPrimerTabState';
import EditPrimerTemplate from './EditPrimerTemplate';
import EmptySequencePrimerTemplate from './EmptySequencePrimerTemplate';
import ExistingPrimersTemplate from './ExistingPrimersTemplate';
import PreviousVersionPrimerTemplate from './PreviousVersionPrimerTemplate';
// @ts-expect-error
import SecondaryStructure from './SecondaryStructure';
import SecondaryStructureTabState from './SecondaryStructureTabState';

interface OptionsView extends Backbone.View<EditPrimerTabState> {
  sequence: ComponentSequence;
  _sequence: BioJSSequence;
  selection: Selection;
  template: typeof EditPrimerOptionsTemplate;
}

class OptionsView extends Backbone.View<EditPrimerTabState> {
  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'navbar edit-primer-options';
    this.prototype.template = EditPrimerOptionsTemplate;
  }

  override events(): Backbone.EventsHash {
    return {
      'click .js-read-selection-forward': (e) => {
        this.readSelectionClicked(e, 'readSelectionForward');
      },
      'click .js-read-selection-reverse': (e) => {
        this.readSelectionClicked(e, 'readSelectionReverse');
      },
      'click .js-jump': () => {
        this.jumpTo(this.model.get('primer'));
      },
      'click .js-jump-reverse': () => {
        this.jumpTo(this.model.get('primer2')!);
      },
      'change select': 'changeMode',
    };
  }

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this._sequence = this.sequence.get('_sequence');
    this.selection = this.sequence.get('selection');
    this.listenTo(this.selection, 'change', this.renderSelectionButton.bind(this));
    this.listenTo(this.model, 'change:primer', this.updatePrimerListeners.bind(this));
    this.listenTo(this.model, 'change:primer2', this.render.bind(this));
    this.listenTo(this.sequence, 'change:currentVersion', this.render.bind(this));
    this.listenTo(this._sequence, 'change:bases', this.render.bind(this));
    this.updatePrimerListeners();
  }

  renderSelectionButton(): void {
    let disableFwd;
    let disableRev;

    if (this.selection.getStartState('primer') != null) {
      disableFwd = false;
    } else {
      disableFwd = isAssemblyPrimer(this._sequence, this.model.get('primer'));
    }
    if (this.selection.getEndState('primer') != null) {
      disableRev = false;
    } else if (this.model.has('primer2')) {
      disableRev = isAssemblyPrimer(this._sequence, this.model.get('primer2')!);
    } else {
      disableRev = isAssemblyPrimer(this._sequence, this.model.get('primer'));
    }
    const disabled = this.selection.isEmpty() || (disableFwd && disableRev);

    this.$('.js-read-selection').toggleClass('disabled', disabled);
    this.$('.js-read-selection-forward').toggleClass('disabled', disableFwd);
    this.$('.js-read-selection-reverse').toggleClass('disabled', disableRev);
  }

  readSelectionClicked(e: JQueryEventObject, triggerEvent: string): void {
    if (!$(e.currentTarget).hasClass('disabled')) {
      this.model.trigger(triggerEvent);
    }
  }

  updatePrimerListeners(): void {
    const primer = this.model.get('primer');
    this.renderModeButton();
    // we want to disable the primer pair mode when the primer is RNA
    this.listenTo(primer, 'change:nucleotideType', this.renderModeButton.bind(this));
  }

  renderModeButton(): void {
    this.$('select').prop('disabled', this.model.get('primer').nucleotideType() === NucleotideType.RNA);
  }

  jumpTo(primer: BioJSSequencePrimer): void {
    const viewPrimer = this.sequence
      .sequencePrimers()
      .find((other) => other.get('_primer').cid === primer.cid);
    viewPrimer.click(this.sequence);
  }

  changeMode(e: JQueryEventObject): void {
    let primer2;
    const numPrimers = parseInt($(e.currentTarget).find('option:selected').attr('value'), 10);
    analytics.track('primer', 'toggle_primer_pair', {
      product: ANALYTICS_MODULE.MOLBIO,
      numPrimers,
      sequence_id: this._sequence.id,
    });
    // Single primer.
    if (numPrimers === 1) {
      this.model.set({primer2: null});
      return;
    }
    // Primer pair.
    assert(numPrimers === 2, 'Expected 2 primers');
    let primer = this.model.get('primer');
    const pair = this.sequence
      .primerPairs()
      .find((pair) => primer.id === pair.forwardPrimerId() || primer.id === pair.reversePrimerId());
    // Primer pair exists.
    if (pair != null) {
      const [fwdPrimer, revPrimer] = pair.getPrimers<SequencePrimerCollection, BioJSSequencePrimer>(
        this._sequence.sequencePrimers()
      );
      this.model.set({primer: fwdPrimer, primer2: revPrimer});
      return;
    }
    // Primer pair does not exist, so create the other primer.
    if (primer.strand() === 1) {
      primer2 = new biojs.SequencePrimer({
        bases: '',
        strand: -1,
        start: 0,
        end: 1,
      });
    } else {
      primer2 = primer;
      primer = new biojs.SequencePrimer({
        bases: '',
        strand: 1,
        start: 0,
        end: 1,
      });
    }
    this.model.set({primer, primer2});
  }

  override render(): OptionsView {
    if (!this.sequence.get('currentVersion') || this.sequence.isEmpty()) {
      this.$el.empty();
      return this;
    }

    // eslint-disable-next-line benchlint/no-eqeqeq-null
    const single = this.model.get('primer2') === null;
    this.$el.html(this.template({single}));
    this.renderSelectionButton();
    this.renderModeButton();
    const value = single ? 1 : 2;
    this.$(`select option[value=${value}]`).prop('selected', true);
    return this;
  }
}

OptionsView.initClass();

interface PrimerParts {
  preRecognitionSite: string;
  recognitionSite: string;
  postRecognitionSite: string;
  stickyEnd: string;
  bindingRegion: string;
}

interface TabView extends Backbone.View<EditPrimerTabState> {
  sequence: ComponentSequence;
  _sequence: BioJSSequence;
  selection: Selection;
  primer: BioJSSequencePrimer;
  primer2: BioJSSequencePrimer | null;
  searchExisting: biojs.SearchPrimerCollection;
  searchExisting2: biojs.SearchPrimerCollection;
  outstandingSearchXhr: JQueryXHR | JQueryPromise<unknown> | null;
  outstandingSearchXhr2: JQueryXHR | JQueryPromise<unknown> | null;
  searchExistingName: [biojs.SearchPrimerCollection, biojs.SearchPrimerCollection];
  outstandingSearchNameXHR: [
    JQueryXHR | JQueryPromise<unknown> | null,
    JQueryXHR | JQueryPromise<unknown> | null,
  ];
  enzymes: Enzymes;
  selectedEnzyme?: Enzyme;
  colorPicker: ColorPicker;
  colorPicker2: ColorPicker;
  dna: ComponentDNA;
  selectorViews: [FolderSelectorView | null, FolderSelectorView | null];
}

class TabView extends Backbone.View<EditPrimerTabState> {
  static initClass(): void {
    this.prototype.className = 'edit-primer buffer-tab';
  }

  override events(): Backbone.EventsHash {
    return {
      'input .js-name': 'updateName',
      'change .primer-libraries': 'updateLibrary',
      'click .js-switch-to-latest': 'switchToLatest',
      'click .swatch-big': 'pickColor',
      'click .js-existing-primer-use': 'useExistingPrimer',
      'click .js-existing-primer-remove': 'removeExistingPrimer',
      'click .js-existing-primer-duplicate': 'duplicateExistingPrimer',
      'input .primer-text': 'saveBases',
      'input .js-overhang-size': (e) => {
        this.updateOverhangSize(this.getPrimer(e));
      },
      'input .js-primer-position': (e) => {
        this.updatePosition(this.getPrimer(e));
      },
      'input .js-binding': (e) => {
        this.updateBinding(this.getPrimer(e));
      },
      'input .js-homology': (e) => {
        this.updateHomology(this.getPrimer(e));
      },
      'input .js-pre-recognition-site': (e) => {
        this.updatePreRecognitionSite(this.getPrimer(e));
      },
      'input .js-post-recognition-site': (e) => {
        this.updatePostRecognitionSite(this.getPrimer(e));
      },
      'change .js-binding, .js-homology, .js-pre-recognition-site, .js-post-recognition-site': (e) => {
        this.saveAssemblyPrimer(this.getPrimer(e));
      },
      'click .js-save': (e) => {
        this.savePrimer(this.getPrimer(e));
        analytics.track('primer', 'save_primers_click', {
          product: ANALYTICS_MODULE.MOLBIO,
          numPrimers: 1,
          sequence_id: this.sequence.get('id'),
        });
      },
      'change .js-strand': 'handleStrandChange',
      'change .js-nucleotide-type': 'handleNucleotideTypeChange',
      'click .js-tm-parameters': () => {
        runModal(MeltingTempModal, {});
      },
      'click .save-pair': 'savePrimerPair',
      'click .js-check-secondary': () => {
        this.removeSecondary();
        this.model.set({checking: true});
        analytics.track('primer', 'check_secondary_structure', {
          product: ANALYTICS_MODULE.MOLBIO,
          numPrimers: this.model.get('primer2') ? 2 : 1,
          sequence_id: this.sequence.get('id'),
        });
      },
      'input .secondary-temp input': 'saveSecondaryTemp',
    };
  }

  override initialize(options: {}): void {
    super.initialize(options);
    this.sequence = this.model.get('sequence');
    this.selection = this.sequence.get('selection');
    this._sequence = this.sequence.get('_sequence');
    this.primer = this.model.get('primer');
    this.primer2 = this.model.get('primer2');

    // Find existing primers.
    this.searchExisting = new biojs.SearchPrimerCollection([], {sequenceID: this.sequence.id});
    this.searchExisting2 = new biojs.SearchPrimerCollection([], {sequenceID: this.sequence.id});
    this.outstandingSearchXhr = null;
    this.outstandingSearchXhr2 = null;

    this.selectorViews = [null, null];
    this.searchExistingName = [new biojs.SearchPrimerCollection(), new biojs.SearchPrimerCollection()];
    this.outstandingSearchNameXHR = [null, null];

    this.listenTo(this.model, 'change:primer2 change:primer', () => {
      this.removeSecondary();
      this.render();
    });
    this.listenTo(this.model, 'change:checking', this.renderSecondary.bind(this));
    this.listenTo(this.model, 'change:secondary', (model: EditPrimerTabState, value: Secondary | null) => {
      if (value) {
        this.renderSecondary();
      }
    });
    this.listenTo(this._sequence, 'change:circular change:bases change:startIndex', () => {
      const prevIsEmpty = this.$('.js-empty-sequence').length > 0;
      const isEmpty = this.sequence.isEmpty();
      if (prevIsEmpty !== isEmpty) {
        this.render();
      } else if (!isEmpty) {
        this.clampPosition(this.primer);
        this.renderPosition(this.primer);
        this.updatePosition(this.primer);
        if (this.primer2 != null) {
          this.clampPosition(this.primer2);
          this.renderPosition(this.primer2);
          this.updatePosition(this.primer2);
        }
        this.renderProductLength();
      }
    });
    this.listenTo(this.sequence, 'change:currentVersion', this.render.bind(this));

    this.enzymes = Enzymes.getCommonEnzymes();

    // TODO (BNCH-47280): Replace ColorPicker with react version
    this.colorPicker = new ColorPicker();
    this.colorPicker2 = new ColorPicker();
    this.listenTo(this.model, 'readSelectionForward', this.readSelectionForward.bind(this));
    this.listenTo(this.model, 'readSelectionReverse', this.readSelectionReverse.bind(this));
    this.listenTo(this.model, 'change:analyzeSecondary', this.autoSecondaryCheck.bind(this));
    this.listenTo(this.model, 'change:temperature', this.renderSecondaryTemp.bind(this));

    if (this.primer.isNew()) {
      this.primer.set('folder_api_id', FolderDefaults.get(FolderDefaultsKey.OLIGO)!);
    }
    if (this.primer2?.isNew()) {
      this.primer2.set('folder_api_id', FolderDefaults.get(FolderDefaultsKey.OLIGO)!);
    }
    const primers = this.sequence.sequencePrimers();
    this.listenTo(primers, 'reset', () => {
      const updates: Partial<EditPrimerTabStateAttributes> = {};
      const primer = primers.get(this.primer.id);
      if (primer != null) {
        updates.primer = primer.get('_primer');
      }
      if (this.primer2 != null) {
        const primer2 = primers.get(this.primer2.id);
        if (primer2 != null) {
          updates.primer2 = primer2.get('_primer');
        }
      }
      this.model.set(updates);
    });

    this.findExistingPrimer(this.primer);
    if (this.primer2 != null) {
      this.findExistingPrimer(this.primer2);
    }
  }

  clampPosition(primer: BioJSSequencePrimer): void {
    const position = primer.bindPosition(this._sequence);
    const newPosition = Math.min(position, this._sequence.length() - 1);
    if (position !== newPosition) {
      primer.setBindPosition(this._sequence, newPosition, primer.overhangLength());
    }
  }

  override remove(): TabView {
    super.remove();
    this.selectorViews[0]?.remove();
    this.selectorViews[1]?.remove();
    return this;
  }

  switchToLatest(e: JQueryEventObject): void {
    e.preventDefault();
    const versions = this.sequence.get('versions');
    const lastVersion = versions.max((v) => v.get('id'));
    versions.trigger('open', lastVersion);
  }

  saveSecondaryTemp(e: JQueryEventObject): void {
    const temperature = parseInt($(e.currentTarget).val(), 10);
    if (Number.isNaN(temperature)) {
      this.model.unset('temperature');
    } else {
      this.model.set({temperature});
    }
  }

  renderSecondaryTemp(): void {
    const $input = this.$('.secondary-temp input');
    if (this.model.has('temperature')) {
      $input.val(this.model.get('temperature') as number);
    } else {
      $input.val('');
    }
  }

  updateName(e: JQueryEventObject): void {
    this.getPrimer(e)?.set({name: $(e.currentTarget).val()});
  }

  renderName(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const $name = this.$('.js-name').eq(fieldIndex);
    if ($name.val() !== primer.name()) {
      $name.val(primer.name());
    }
  }

  updateLibrary(e: JQueryEventObject): void {
    const primer = this.getPrimer(e);
    const inputValue = (e.target as HTMLInputElement).value;
    primer.set({folder_api_id: inputValue});
    FolderDefaults.set(FolderDefaultsKey.OLIGO, inputValue);
  }

  saveSelection($primerText: JQuery): null | [number, number] {
    if (document?.activeElement?.className !== 'primer-text') {
      return null;
    }
    const containerEl = $primerText[0];
    const range = selutil.getRange();
    if (range == null) {
      return null;
    }
    let start = range.startOffset;
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(containerEl);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    start = preSelectionRange.toString().length;

    return [start, start + range.toString().length];
  }

  restoreSelection($primerText: JQuery, saved: [number, number] | null): void {
    if (saved == null) {
      return;
    }

    const [savedStart, savedEnd] = saved;
    const containerEl: Node = $primerText[0];
    let charIndex = 0;
    const range = document.createRange();
    range.setStart(containerEl, 0);
    range.collapse(true);
    const nodeStack = [containerEl];
    let foundStart = false;

    let node = nodeStack.pop();
    while (node) {
      // check if node is Text node
      if (node.nodeType === 3) {
        const nextCharIndex = charIndex + (node as Text).length;
        if (!foundStart && savedStart >= charIndex && savedStart <= nextCharIndex) {
          range.setStart(node, savedStart - charIndex);
          foundStart = true;
        }

        if (foundStart && savedEnd >= charIndex && savedEnd <= nextCharIndex) {
          range.setEnd(node, savedEnd - charIndex);
          break;
        }

        charIndex = nextCharIndex;
      } else {
        let i = node.childNodes.length;
        while (i--) {
          nodeStack.push(node.childNodes[i]);
        }
      }
      node = nodeStack.pop();
    }

    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  saveBases(e: JQueryEventObject): void {
    const primer = this.getPrimer(e);
    const primerBases = util.sanitize($(e.currentTarget).text(), primer.nucleotideType());
    if (primerBases === primer.bases()) {
      this.showBases(primer);
    } else {
      primer.changeBases(this._sequence, primerBases);
    }
    this.updatePosition(primer);
  }

  showBases(primer: BioJSSequencePrimer): void {
    const $primerText = this.$('.primer-text').eq(this.fieldIndex(primer));
    const saved = this.saveSelection($primerText);
    const isGoldenGate = isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GOLDEN_GATE);
    const assembly = this._sequence.assembly();
    const basesHtml = ReactDOMServer.renderToStaticMarkup(
      <PrimerBases
        baseMatchingStatuses={primer.baseMatchingStatuses(this._sequence)}
        bases={primer.bases()}
        {...(assembly != null && isGoldenGate
          ? {
              isGoldenGate: true,
              enzyme: assembly.enzyme(),
            }
          : {isGoldenGate: false})}
        overhangLength={primer.overhangLength()}
      />
    );
    $primerText.html(basesHtml);
    this.restoreSelection($primerText, saved);
    if (isAssemblyPrimer(this._sequence, primer)) {
      $primerText.removeAttr('contenteditable');
      $primerText.tooltip({
        placement: 'bottom',
        title: 'Perform edits for assembly primers below or on the sequence map.',
        delay: 500,
      });
    } else {
      // @ts-expect-error we sometimes pass booleans to .attr()
      $primerText.attr('contenteditable', true);
      $primerText.tooltip('destroy');
    }
  }

  pickColor(e: JQueryEventObject): void {
    const fieldIndex = this.fieldIndex(this.getPrimer(e));
    this.$('.swatch-big').eq(fieldIndex).popover('show');
  }

  initializeFields(primer: BioJSSequencePrimer, fieldIndex: 0 | 1): void {
    const strandString = primer.strand() === 1 ? 'forward' : 'reverse';
    this.$('.js-strand')
      .filter((id: number, element: Element) => (element as HTMLInputElement).value === strandString)
      .prop('checked', true);

    this.$('.js-nucleotide-type')
      .filter(
        (id: number, element: Element) => (element as HTMLInputElement).value === primer.nucleotideType()
      )
      .prop('checked', true);

    this.$('.js-name').eq(fieldIndex).val(primer.name());
    this.$('.swatch-big').eq(fieldIndex).css('background-color', primer.color());
    const overhangSize = primer.overhangBases().length;
    const $overhangSize = this.$('.js-overhang-size').eq(fieldIndex);
    // @ts-expect-error sometimes we pass null to .val()
    $overhangSize.val(overhangSize > 0 ? overhangSize : null);
    const $primerPosition = this.$('.js-primer-position').eq(fieldIndex);
    $primerPosition.val(
      sequtil.modelToDisplay(primer.bindPosition(this._sequence), this._sequence.startIndex())
    );
  }

  readSelectionForward(): void {
    this._readSelection(1);
  }

  readSelectionReverse(): void {
    this._readSelection(-1);
  }

  _readSelection(strand: StrandOfDoubleStranded): void {
    const forwardStrand = strand === 1;
    const selectedPrimers = [this.selection.getStartState('primer'), this.selection.getEndState('primer')];
    // eslint-disable-next-line no-restricted-syntax
    const primer = _.find(selectedPrimers, (selectedPrimer) => selectedPrimer?.strand() === strand);
    const selStart = this.selection.get('start');
    const selEnd = this.selection.get('end')!;
    if (primer && !primer.isNew() && primer.start() === selStart && primer.end() === selEnd) {
      if (forwardStrand || !this.primer2) {
        this.model.set({primer: primer.get('_primer')});
      } else {
        this.model.set({primer2: primer.get('_primer')});
      }
    } else {
      const position = forwardStrand ? selEnd - 1 : selStart;
      const doubleBases = this.dna.slice(selStart, selEnd);
      const primer = forwardStrand || !this.primer2 ? this.primer : this.primer2;
      // if reading bases from the reverse strand, then we need to reverse the complement of the selected bases
      const dnaBases = forwardStrand ? doubleBases[0].bases : util.reverse(doubleBases[1].bases);
      // possibly convert DNA bases to RNA
      const sanitizedBases = util.sanitize(dnaBases, primer.nucleotideType());
      primer.changeBases(this._sequence, sanitizedBases);
      primer.setBindPosition(this._sequence, position, 0);
      const fieldIndex = this.fieldIndex(primer);
      this.$('.js-overhang-size')
        .eq(fieldIndex)
        // @ts-expect-error
        .val(null);

      if (this.primer2 == null) {
        const strandString = strand === 1 ? 'forward' : 'reverse';
        this.$('.js-strand')
          .filter((id: number, element: Element) => (element as HTMLInputElement).value === strandString)
          .prop('checked', true);
        this.updateStrand(strand);
      }
    }
  }

  closePicker(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const $swatch = this.$('.swatch-big').eq(fieldIndex);
    $swatch.css('background-color', primer.color());
    const colorPicker = fieldIndex === 0 ? this.colorPicker : this.colorPicker2;
    colorPicker.update(primer);
    $swatch.popover('hide');
  }

  _getExistingPrimer($dataEl: JQuery, searchExisting: SearchPrimerCollection): BioJSSequencePrimer {
    const primerId = $dataEl.data('primerId');
    return searchExisting.findWhere({primer_id: primerId});
  }

  useExistingPrimer(e: JQueryEventObject): void {
    const primer =
      $(e.currentTarget).parents('.js-found-existing-primer').data('index') === 1
        ? this.primer2
        : this.primer;
    const searchExisting = primer === this.primer ? this.searchExisting : this.searchExisting2;
    const existingPrimer = this._getExistingPrimer($(e.currentTarget), searchExisting);
    primer!.set({
      bases: existingPrimer.bases(),
      primer_id: existingPrimer.get('primer_id'),
      folder: existingPrimer.get('folder'),
      owner: existingPrimer.get('owner'),
      name: existingPrimer.name(),
      // @ts-expect-error
      folder_api_id: existingPrimer.get('folder').api_identifier,
      primer_api_id: existingPrimer.get('primer_api_id'),
      color: existingPrimer.color(),
    });
  }

  renderMatches(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const $foundExistingPrimer = this.$('.js-found-existing-primer').eq(fieldIndex);
    const searchExisting = fieldIndex === 0 ? this.searchExisting : this.searchExisting2;
    if (searchExisting.length > 0) {
      $foundExistingPrimer
        .html(
          // eslint-disable-next-line new-cap
          ExistingPrimersTemplate({
            primers: searchExisting.models,
            single: this.primer2 == null,
            used: false,
            isAssemblyPrimer: false,
          })
        )
        .removeClass('invisible');
      this.$('.js-use-existing-primer').eq(fieldIndex).addClass('invisible');
      this.toggleGroup('.js-use-existing-primer');
    } else {
      $foundExistingPrimer.addClass('invisible');
    }
    this.toggleGroup('.js-found-existing-primer');
  }

  findExistingPrimer(primer: BioJSSequencePrimer): void {
    if (primer.has('id')) {
      return;
    }
    const fieldIndex = this.fieldIndex(primer);
    const searchExisting = fieldIndex === 0 ? this.searchExisting : this.searchExisting2;

    let xhr = fieldIndex === 0 ? this.outstandingSearchXhr : this.outstandingSearchXhr2;
    // @ts-expect-error JQueryPromise<unknown> lacks .abort() but optional chaining guards against errors
    xhr?.abort?.();
    xhr = searchExisting
      .fetch({data: {bases: primer.bases(), nucleotideType: primer.nucleotideType()}, parse: true})
      .done(() => {
        this.renderMatches(primer);
      });
    if (fieldIndex === 0) {
      this.outstandingSearchXhr = xhr;
    } else {
      this.outstandingSearchXhr2 = xhr;
    }
  }

  findExistingName(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const searchExisting = this.searchExistingName[fieldIndex];
    let xhr = this.outstandingSearchNameXHR[fieldIndex];
    // @ts-expect-error JQueryPromise<unknown> lacks .abort() but optional chaining guards against errors
    xhr?.abort?.();

    if (primer.has('primer_id') || primer.get('folder_api_id') == null) {
      searchExisting.reset();
      xhr = $.Deferred().resolve().promise();
    } else {
      xhr = searchExisting.fetch(
        {
          data: {
            name: primer.name(),
            nucleotideType: primer.nucleotideType(),
            exact: 1,
            limit: 1,
            folderId: primer.get('folder_api_id'),
          },
        },
        // @ts-expect-error possibly an extra arg
        {parse: true}
      );
    }

    xhr.done(() => {
      const $foundExistingName = this.$('.js-found-existing-name').eq(fieldIndex);
      $foundExistingName.find('.existing-name').text(primer.name());
      $foundExistingName.toggleClass('invisible', searchExisting.isEmpty());
      this.toggleGroup('.js-found-existing-name');
    });

    this.outstandingSearchNameXHR[fieldIndex] = xhr;
  }

  checkNameLength(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const $nameTooLong = this.$('.js-name-too-long').eq(fieldIndex);
    $nameTooLong.toggleClass('invisible', !this.isNameTooLong(primer));
  }

  duplicateExistingPrimer(e: JQueryEventObject): void {
    let field: 'primer' | 'primer2';
    let original;
    if ($(e.currentTarget).parents('.js-use-existing-primer').data('index') === 1) {
      field = 'primer2';
      original = this.model.originalPrimer2;
    } else {
      field = 'primer';
      original = this.model.originalPrimer;
    }
    const primer = this[field] as BioJSSequencePrimer;
    const newPrimer = primer.clone();
    primer.set(original as SequencePrimerAttributes);
    newPrimer.unset('id');
    newPrimer.unset('primer_id');
    newPrimer.unset('folder');
    newPrimer.unset('folder_api_id');
    newPrimer.unset('owner');
    newPrimer.unset('primer_api_id');
    newPrimer.set({name: `${newPrimer.get('name')} copy`});
    this.model.set(field, newPrimer);
  }

  renderSecondary(): void {
    const noPrimerBases =
      this.primer.bases().length === 0 && (this.primer2 == null || this.primer2.bases().length === 0);

    const disableCheckSecondary = (disabled: boolean): JQuery => {
      disabled = disabled || noPrimerBases;
      return this.$('.js-check-secondary').toggleClass('is-disabled', disabled).prop('disabled', disabled);
    };

    if (this.model.get('checking')) {
      this.$('.js-secondary').removeClass('hidden');

      if (this.model.has('secondary')) {
        const secondary = this.model.get('secondary')!;
        const getDG = (
          info: {dg: number; svg: string},
          name: string,
          nucleotideType: NucleotideType
        ): string =>
          // @ts-expect-error we sometimes use JQuery as htmlString
          $('<a>')
            .addClass('structure')
            .text(`${info.dg} kcal`)
            .click(() => {
              this.renderSvg(info.svg, info.dg, name, nucleotideType);
            });
        const showInfo = (
          filter: (node: JQuery) => JQuery,
          mkey: 'monomer' | 'monomer2',
          hkey: 'homodimer' | 'homodimer2',
          nucleotideType?: NucleotideType,
          bases?: string
        ): void => {
          const $monomer = filter(this.$('.js-monomer-dg'));
          const $monomerLink = filter(this.$('.js-monomer'));
          if (secondary[mkey] != null) {
            $monomer.html(getDG(secondary[mkey], 'Monomer', nucleotideType!));
            $monomerLink.removeClass('invisible');
            $monomerLink.click(() => {
              this.findAllStructures($monomerLink, secondary[mkey]!, 'Monomer', nucleotideType!, bases!);
            });
          } else {
            $monomer.html('--');
            $monomerLink.addClass('invisible');
            $monomerLink.unbind('click');
          }
          const $homodimer = filter(this.$('.js-homodimer-dg'));
          const $homodimerLink = filter(this.$('.js-homodimer'));
          if (secondary[hkey] != null) {
            $homodimer.html(getDG(secondary[hkey], 'Homodimer', nucleotideType!));
            $homodimerLink.removeClass('invisible');
            $homodimerLink.click(() => {
              this.findAllStructures(
                $homodimerLink,
                secondary[hkey]!,
                'Homodimer',
                nucleotideType!,
                bases!,
                bases
              );
            });
          } else {
            $homodimer.html('--');
            $homodimerLink.addClass('invisible');
            $homodimerLink.unbind('click');
          }
        };

        showInfo(
          (node) => node.first(),
          'monomer',
          'homodimer',
          this.primer.nucleotideType(),
          this.primer.bases()
        );
        showInfo(
          (node) => node.filter('[data-index=1]'),
          'monomer2',
          'homodimer2',
          this.primer2?.nucleotideType(),
          this.primer2?.bases()
        );

        if (this.primer2 != null) {
          const $heterodimer = this.$('.js-heterodimer-dg');
          const $heterodimerLink = this.$('.js-heterodimer');
          if (secondary.heterodimer != null) {
            $heterodimer.html(getDG(secondary.heterodimer, 'Heterodimer', this.primer.nucleotideType()));
            $heterodimerLink.removeClass('invisible');
            $heterodimerLink.click(() => {
              this.findAllStructures(
                $heterodimerLink,
                secondary.heterodimer!,
                'Heterodimer',
                this.primer.nucleotideType(),
                this.primer.bases(),
                this.primer2!.bases()
              );
            });
          } else {
            $heterodimer.html('--');
            $heterodimerLink.addClass('invisible');
            $heterodimerLink.unbind('click');
          }
        }
        disableCheckSecondary(false);
      } else {
        disableCheckSecondary(true);
      }
    } else {
      this.$('.js-secondary').addClass('hidden');
      disableCheckSecondary(false);
      // @ts-expect-error we sometimes use JQuery as htmlString
      this.$('.js-secondary .input-field').html($('<div>').addClass('spinner'));
      this.$('.secondary-link').addClass('invisible');
    }
  }

  findAllStructures(
    $link: JQuery,
    mainStructure: {dg: number; svg: string},
    name: string,
    nucleotideType: NucleotideType,
    primer: string,
    primer2?: string
  ): void {
    const formData = new FormData();
    formData.append('all', true.toString());
    formData.append('nucleotideType', nucleotideType);
    formData.append('primer', primer);
    if (primer2 != null) {
      formData.append('primer2', primer2);
    }
    formData.append('temperature', this.model.get('temperature')?.toString() ?? '37');
    // @ts-expect-error we sometimes use JQuery as htmlString
    $link.html($('<div>').addClass('spinner'));
    const xhr = TaskRunner.runTask({
      url: '/1/api/oligo-secondary-structure',
      data: formData,
      dataType: 'json',
      processData: false,
      contentType: false,
      type: 'PUT',
    });
    xhr
      .fail((res) => {
        notifications.notifyError(res);
      })
      .done((res) => {
        SecondaryStructureTabState.create(
          this.model.get('sequence'),
          mainStructure,
          res,
          name,
          this.model.get('temperature')!,
          nucleotideType,
          primer,
          primer2
        );
        // @ts-expect-error we sometimes use JQuery as htmlString
        $link.html($('<a>').text('All Structures'));
      });
  }

  renderSvg(svg: string, dg: number, name: string, nucleotideType: NucleotideType): JQuery {
    const model = new SecondaryStructure({svg, dg, name, nucleotideType});
    return model.showModal();
  }

  removeSecondary(): void {
    this.model.set({checking: false});
    this.model.unset('secondary');
  }

  removeExistingPrimer(e: JQueryEventObject): void {
    const primer =
      $(e.currentTarget).parents('.js-use-existing-primer').data('index') === 1 ? this.primer2 : this.primer;
    primer?.unset('primer_id');
    primer?.unset('primer_api_id');
  }

  renderUsedPrimer(primer: BioJSSequencePrimer): void {
    if (!primer.has('primer_id')) {
      this.renderMatches(primer);
    } else {
      const fieldIndex = this.fieldIndex(primer);
      this.$('.js-use-existing-primer')
        .eq(fieldIndex)
        .html(
          // eslint-disable-next-line new-cap
          ExistingPrimersTemplate({
            primers: [primer],
            single: this.primer2 == null,
            used: true,
            isAssemblyPrimer: isAssemblyPrimer(this._sequence, primer),
          })
        )
        .removeClass('invisible');
      this.toggleGroup('.js-use-existing-primer');
      this.$('.js-found-existing-primer').eq(fieldIndex).addClass('invisible');
      this.toggleGroup('.js-found-existing-primer');
    }
  }

  renderLibraries(): void {
    this.selectorViews[0]?.remove();
    this.selectorViews[1]?.remove();
    this.selectorViews = [null, null];
    this.selectorViews[0] = new FolderSelectorView({
      initialFolderId: this.primer.get('folder_api_id'),
      inputClassName: 'primer-libraries',
      props: {showInventory: true, enableRedesign: true},
    });
    this.$('.folder-selector-container').eq(0).html(this.selectorViews[0].render().el);
    if (this.primer2 != null) {
      this.selectorViews[1] = new FolderSelectorView({
        initialFolderId: this.primer2.get('folder_api_id'),
        inputClassName: 'primer-libraries',
        inputData: {index: 1},
        props: {showInventory: true, enableRedesign: true},
      });
      this.$('.folder-selector-container').eq(1).html(this.selectorViews[1].render().el);
    }
  }

  listenToPrimer(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    this.listenTo(this.primer, 'change:start change:end change:bases', () => {
      const $overhangSize = this.$('.js-overhang-size').eq(fieldIndex);
      if ($overhangSize.val().length === 0) {
        $overhangSize.val(0);
      }
    });
    this.listenTo(
      primer,
      'change:start change:end change:bases change:overhang_length',
      this.renderStats.bind(this, primer)
    );
    this.listenTo(primer, 'change:primer_id', this.renderNucleotideTypeRadioButton.bind(this));
    this.listenTo(primer, 'change:strand change:start change:end', this.renderPosition.bind(this, primer));
    this.listenTo(primer, 'change', this.renderSaveButton.bind(this, primer));
    this.listenTo(this.model, 'change:saving', this.renderSaveButton.bind(this, primer));
    this.listenTo(primer, 'change', this.renderSavePair.bind(this));
    this.listenTo(this.model, 'change:saving', this.renderSavePair.bind(this));
    this.listenTo(primer, 'change:color', this.closePicker.bind(this, primer));
    this.listenTo(primer, 'change:bases', this.renderBaseMsg.bind(this, primer));
    this.listenTo(
      primer,
      'change:bases change:overhang_length change:start change:end change:strand',
      this.showBases.bind(this, primer)
    );
    const debouncedFindExisting = debounce(this.findExistingPrimer.bind(this, primer), 500);
    this.listenTo(primer, 'change:bases change:nucleotideType', () => {
      if (
        primer.get('bases').toUpperCase() === primer.previous('bases').toUpperCase() &&
        primer.get('nucleotideType') === primer.previous('nucleotideType')
      ) {
        return;
      }
      debouncedFindExisting();
    });
    this.listenTo(
      primer,
      'change:name change:folder_api_id change:primer_id',
      debounce(this.findExistingName.bind(this, primer), 500)
    );
    this.listenTo(primer, 'change:name', debounce(this.checkNameLength.bind(this, primer), 500));
    this.listenTo(
      primer,
      'change:name change:folder_api_id change:primer_id',
      this.renderUsedPrimer.bind(this, primer)
    );
    this.listenTo(primer, 'change:bases', this.renderSecondary.bind(this));
    this.listenTo(primer, 'change:name', this.renderName.bind(this, primer));
    this.listenTo(primer, 'change:bases', this.removeSecondary.bind(this));
    this.listenTo(
      primer,
      'change:bases change:start change:end change:overhang_length',
      this.renderAssemblyFieldValues.bind(this, primer)
    );
  }

  registerListeners(): void {
    let primer;
    this.listenToPrimer(this.primer);
    if (this.primer2 != null) {
      this.listenToPrimer(this.primer2);

      for (primer of [this.primer, this.primer2]) {
        this.listenTo(
          primer,
          'change:bases change:start change:end change:overhang_length',
          this.renderMeltingTempDiff.bind(this)
        );
        this.listenTo(
          primer,
          'change:bases change:start change:end change:overhang_length',
          this.renderProductLength.bind(this)
        );
      }
    }
    this.listenTo(
      preferences,
      `change:${PrimerPreferences.MELTING_TEMP_DNA} change:${PrimerPreferences.MELTING_TEMP_RNA}`,
      () => {
        this.renderStats(this.primer);
        if (this.primer2 != null) {
          this.renderStats(this.primer2);
          this.renderMeltingTempDiff();
        }
      }
    );
  }

  computeProductLength(): number {
    return util.productLength(
      [this.primer.start(), this.primer.end()],
      [this.primer2!.start(), this.primer2!.end()],
      this._sequence.circular(),
      this._sequence.length()
    );
  }

  renderProductLength(): void {
    const [forward, reverse] = [this.primer, this.primer2];
    let isValid = true;
    if (reverse != null) {
      let length;
      if (forward.length() === 0 || reverse.length() === 0) {
        length = '--';
      } else {
        const productLength = this.computeProductLength();
        isValid = productLength > 0;
        length = `${productLength + forward.overhangBases().length + reverse.overhangBases().length} bp`;
      }
      this.$('.js-product-size').html(length);
    }
    this.$('.js-invalid-pair').toggleClass('hidden', isValid);
  }

  renderBaseMsg(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    this.$('.too-short-msg').eq(fieldIndex).toggleClass('invisible', primer.validBases());
    this.toggleGroup('.too-short-msg');
  }

  checkDimerization(): void {
    notifications.error(
      "We're currently experiencing problems calculating dimerization and have temporarily disabled the service while we roll out a fix. We apologize for the inconvenience."
    );
  }

  handleStrandChange(e: JQueryEventObject): void {
    const strand = $(e.currentTarget).val() === 'forward' ? 1 : -1;
    this.updateStrand(strand);
  }

  handleNucleotideTypeChange(e: JQueryEventObject): void {
    const nucleotideType = $(e.currentTarget).val();
    this.primer.set({nucleotideType});
  }

  updateStrand(strand: number, primer?: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    if (strand !== primer.strand()) {
      primer.reverse(this._sequence);
      this.updateOverhangSize(primer);
    }
  }

  _readPosition(primer: BioJSSequencePrimer): number {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $position = this.$('.js-primer-position').eq(fieldIndex);
    const displayPosition = parseInt($position.val(), 10);
    const seqLength = this.model.get('sequence').length();
    if (Number.isNaN(displayPosition)) {
      return NaN;
    }
    const position = sequtil.displayToModel(displayPosition, this._sequence.startIndex());
    if (position < 0 || position >= seqLength) {
      return NaN;
    }
    return position;
  }

  updatePosition(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    if (isAssemblyPrimer(this._sequence, primer)) {
      return;
    }
    const fieldIndex = this.fieldIndex(primer);
    const position = this._readPosition(primer);
    const $badLocationMsg = this.$('.bad-location-msg').eq(fieldIndex);
    $badLocationMsg.toggleClass('invisible', !Number.isNaN(position));
    this.toggleGroup('.bad-location-msg');
    const location = sequtil.modelToDisplay(primer.bindPosition(this._sequence), this._sequence.startIndex());
    $badLocationMsg.find('.location-num').text(location);
    if (Number.isNaN(position)) {
      return;
    }
    this.toggleGroup('.bad-location-msg');
    primer.setBindPosition(this._sequence, position, primer.get('overhang_length'));
    this.updateOverhangSize(primer);
  }

  saveAssemblyPrimer(primer: BioJSSequencePrimer): void {
    primer.debouncedSave({
      done: () => {
        this.sequence.updateVersions();
        notifications.success('Assembly primer successfully updated.', {
          id: 'EditPrimerView.saveAssemblyPrimer.done',
        });
      },
      fail: notifications.notifyError,
      invalid: notifications.error,
    });
  }

  _getGoldenGatePrimerParts(primer: BioJSSequencePrimer): PrimerParts {
    const enzyme = this.model.getAssemblyEnzyme() as Enzyme;
    const recognitionSiteStart = primer.bases().indexOf(enzyme.get('seq'));
    const postRecognitionSiteStart = recognitionSiteStart + enzyme.recognitionSiteLength();
    const stickyEndStart = postRecognitionSiteStart + enzyme.postRecognitionSiteLength();
    const bindingRegionStart = stickyEndStart + enzyme.stickyEndLength();
    const bases = primer.bases();
    return {
      preRecognitionSite: bases.slice(0, recognitionSiteStart),
      recognitionSite: bases.slice(recognitionSiteStart, postRecognitionSiteStart),
      postRecognitionSite: bases.slice(postRecognitionSiteStart, stickyEndStart),
      stickyEnd: bases.slice(stickyEndStart, bindingRegionStart),
      bindingRegion: bases.slice(bindingRegionStart),
    };
  }

  _joinPrimerParts(primerParts: PrimerParts): string {
    const {preRecognitionSite, recognitionSite, postRecognitionSite, stickyEnd, bindingRegion} = primerParts;
    return preRecognitionSite + recognitionSite + postRecognitionSite + stickyEnd + bindingRegion;
  }

  _updateGoldenGatePrimer(primer: BioJSSequencePrimer, primerPartsToReplace: Partial<PrimerParts>): void {
    const currentPrimerParts = this._getGoldenGatePrimerParts(primer);
    // eslint-disable-next-line no-restricted-syntax
    const primerParts = _.extend({}, currentPrimerParts, primerPartsToReplace);

    // Always validate.
    const enzyme = this.model.getAssemblyEnzyme() as Enzyme;
    const fieldIndex = this.fieldIndex(primer);
    const {preRecognitionSite, postRecognitionSite} = primerParts;

    const invalidBases = preRecognitionSite.indexOf(enzyme.get('seq')) > -1;
    const $invalidBases = this.$('.pre-recognition-site-invalid-bases-msg').eq(fieldIndex);
    $invalidBases.toggleClass('invisible', !invalidBases);
    this.toggleGroup('.pre-recognition-site-invalid-bases-msg');

    const invalidLength = postRecognitionSite.length !== enzyme.postRecognitionSiteLength();
    const $invalidLength = this.$('.post-recognition-site-invalid-length-msg').eq(fieldIndex);
    $invalidLength.toggleClass('invisible', !invalidLength);
    this.toggleGroup('.post-recognition-site-invalid-length-msg');
    $invalidLength.find('.valid-length').text(enzyme.postRecognitionSiteLength());

    if (invalidBases || invalidLength) {
      return;
    }

    // Update if it's a valid change.
    if (isEqual(primerParts, currentPrimerParts)) {
      return;
    }
    const bases = this._joinPrimerParts(primerParts);
    // Delta is positive if primer is now longer, negative if primer is now shorter.
    const delta = bases.length - primer.bases().length;
    primer.set({overhang_length: primer.overhangLength() + delta, bases});
    this.saveAssemblyPrimer(primer);
  }

  updatePreRecognitionSite(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $preRecognitionSite = this.$('.js-pre-recognition-site').eq(fieldIndex);
    this._updateGoldenGatePrimer(primer, {preRecognitionSite: $preRecognitionSite.val()});
  }

  updatePostRecognitionSite(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $postRecognitionSite = this.$('.js-post-recognition-site').eq(fieldIndex);
    this._updateGoldenGatePrimer(primer, {postRecognitionSite: $postRecognitionSite.val()});
  }

  updateBinding(primer: BioJSSequencePrimer): void {
    let bases;
    let delta;
    let newEnd;
    let newStart;
    if (primer == null) {
      ({primer} = this);
    }
    const currentBinding = primer.bindingBases().length;
    const fieldIndex = this.fieldIndex(primer);
    const $binding = this.$('.js-binding').eq(fieldIndex);
    const binding = $binding.val() === '' ? 0 : parseInt($binding.val(), 10);
    if (binding === currentBinding) {
      return;
    }
    const length = this.sequence.length();
    if (binding > currentBinding) {
      delta = binding - currentBinding;
      if (primer.strand() === 1) {
        newEnd = sequtil.mod(primer.end() + delta, length);
        bases = primer.bases() + this._sequence.residueSlice(primer.end(), newEnd);
        primer.set({end: newEnd, bases});
      } else {
        assert(primer.strand() === -1, 'Unexpected primer strand');
        newStart = sequtil.mod(primer.start() - delta, length);
        bases =
          primer.bases() +
          util.revcomp(this._sequence.residueSlice(newStart, primer.start()), this._sequence.baseTable());
        primer.set({start: newStart, bases});
      }
    } else {
      delta = currentBinding - binding;
      bases = primer.bases().slice(0, primer.bases().length - delta);
      if (primer.strand() === 1) {
        newEnd = sequtil.mod(primer.end() - delta, length);
        primer.set({end: newEnd, bases});
      } else {
        assert(primer.strand() === -1, 'Unexpected primer strand');
        newStart = sequtil.mod(primer.start() + delta, length);
        primer.set({start: newStart, bases});
      }
    }
  }

  updateHomology(primer: BioJSSequencePrimer): void {
    let bases;
    let delta;
    let newOverhangLength;
    if (primer == null) {
      ({primer} = this);
    }
    const currentHomology = primer.overhangLength();
    let fieldIndex = this.fieldIndex(primer);
    fieldIndex = this.fieldIndex(primer);
    const $homology = this.$('.js-homology').eq(fieldIndex);
    const homology = $homology.val() === '' ? 0 : parseInt($homology.val(), 10);
    if (homology === currentHomology) {
      return;
    }
    const length = this.sequence.length();
    if (homology > currentHomology) {
      delta = homology - currentHomology;
      newOverhangLength = primer.overhangLength() + delta;
      if (primer.strand() === 1) {
        const baseStart = sequtil.mod(primer.start() - primer.overhangLength(), length);
        const newBaseStart = sequtil.mod(baseStart - delta, length);
        bases = this._sequence.residueSlice(newBaseStart, baseStart) + primer.bases();
        primer.set({bases, overhang_length: newOverhangLength});
      } else {
        assert(primer.strand() === -1, 'Unexpected primer strand');
        const baseEnd = sequtil.mod(primer.end() + primer.overhangLength(), length);
        const newBaseEnd = sequtil.mod(baseEnd + delta, length);
        bases =
          util.revcomp(this._sequence.residueSlice(baseEnd, newBaseEnd), this._sequence.baseTable()) +
          primer.bases();
        primer.set({bases, overhang_length: newOverhangLength});
      }
    } else {
      delta = currentHomology - homology;
      newOverhangLength = primer.overhangLength() - delta;
      bases = primer.bases().slice(delta, primer.bases().length);
      primer.set({bases, overhang_length: newOverhangLength});
    }
  }

  toggleGroup(className: string): void {
    this.$el
      .find(`${className}-group`)
      .toggleClass('hidden', this.$(`${className}-group ${className}:not(.invisible)`).length === 0);
  }

  fieldIndex(primer: BioJSSequencePrimer | null): 0 | 1 {
    if (primer === this.primer) {
      return 0;
    } else {
      return 1;
    }
  }

  getPrimer(e: JQueryEventObject): BioJSSequencePrimer {
    if ($(e.currentTarget).data('index') === 1) {
      return this.primer2!;
    } else {
      return this.primer;
    }
  }

  updateOverhangSize(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $overhangSize = this.$('.js-overhang-size').eq(fieldIndex);
    const overhangSize = $overhangSize.val().length > 0 ? parseInt($overhangSize.val(), 10) : 0;
    const minMaxOverhang = this.getMinMaxOverhang(primer);
    if (minMaxOverhang[0] <= overhangSize && overhangSize <= minMaxOverhang[1]) {
      primer.setOverhang(this._sequence, overhangSize);
    }
    this.renderOverhangMessage(primer);
  }

  getMinMaxOverhang(primer: BioJSSequencePrimer): [number, number] {
    let minOverhang;
    if (primer == null) {
      ({primer} = this);
    }
    const maxOverhang = primer.bases().length - 1;
    if (this.model.get('sequence').circular()) {
      minOverhang = primer.bases().length - this.model.get('sequence').length();
    } else if (primer.strand() === 1) {
      minOverhang = primer.bases().length - (primer.bindPosition(this._sequence) + 1);
    } else {
      minOverhang = primer.start() + primer.bases().length - this.sequence.length();
    }
    return [Math.max(0, minOverhang), Math.max(0, maxOverhang)];
  }

  handleSavedPrimer(primer: BioJSSequencePrimer, close: boolean = true): void {
    if (close) {
      notifications.success(
        <>
          Primer <strong>{primer.get('name')}</strong> successfully saved.
        </>
      );
      this.model.close(false);
    }
    this._sequence.sequencePrimers().add(primer);
    this._sequence.sequencePrimers().each((otherPrimer) => {
      if (primer.get('primer_id') === otherPrimer.get('primer_id') && primer.id !== otherPrimer.id) {
        otherPrimer.set({
          bases: primer.bases(),
          color: primer.get('color'),
          name: primer.get('name'),
          folder_api_id: primer.get('folder_api_id'),
        });
      }
    });
    this.sequence.sequencePrimers().get(primer.id).click(this.model.get('sequence'));
    FolderDAOCache.invalidateFolderContents(primer.get('folder_api_id'));
    FolderDAO.fetch(primer.get('folder_api_id')!, {summary: '1'}).then((folder) => {
      if (!RegistryUtil.isRegistry(folder!.source)) {
        FolderDefaults.set(FolderDefaultsKey.OLIGO, primer.get('folder_api_id')!);
      }
    });
  }

  trackSavePrimersFailure(res: Response, numPrimers: number): void {
    notifications.notifyError(res);
    analytics.track('primer', 'save_primers_fail', {
      product: ANALYTICS_MODULE.MOLBIO,
      numPrimers,
      error: notifications.getFriendlyError(res),
      sequence_id: this._sequence.get('id'),
    });
  }

  trackSavePrimersSuccess(numPrimers: number, primerApiIds: Array<string>): void {
    analytics.track('primer', 'save_primers_success', {
      product: ANALYTICS_MODULE.MOLBIO,
      numPrimers,
      primerApiIds,
      sequence_id: this._sequence.get('id'),
    });
  }

  _savePrimerHelper(primer: BioJSSequencePrimer): void {
    const savePrimer = (): JQueryPromise<void> | undefined => {
      this.model.saving(this.fieldIndex(primer), true);
      const rv = primer.save({}, {fileUpdate: true});
      // @ts-expect-error
      assertAndContinue(rv, `Managed to savePrimer with invalid primer: ${primer.validationError}`);
      if (!rv) {
        notifications.error('An unexpected error has occurred.');
        return;
      }
      return rv
        .fail((res) => {
          this.trackSavePrimersFailure(res, 1);
        })
        .done(() => {
          this.trackSavePrimersSuccess(1, [primer.get('primer_api_id')!]);
          this.handleSavedPrimer(primer);
        })
        .always(() => {
          this.model.saving(this.fieldIndex(primer), false);
          this.sequence.updateVersions();
        });
    };
    if (this.isNameTooLong(primer)) {
      return;
    }
    if (primer.has('primer_api_id')) {
      checkPrimerUtil.checkPrimer(
        primer.get('primer_api_id') ?? null,
        primer.bases(),
        savePrimer,
        this._sequence.id,
        (res: Response) => {
          this.trackSavePrimersFailure(res, 1);
        }
      );
    } else {
      savePrimer();
    }
  }

  savePrimer(primer?: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    this._savePrimerHelper(primer);
  }

  savePrimerPair(): void {
    let deferred: JQueryDeferred<unknown>;
    analytics.track('primer', 'save_primers_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      numPrimers: 2,
      sequence_id: this.sequence.get('id'),
    });
    const handlePair = (): JQueryPromise<unknown> => {
      const existing = this.sequence.primerPairs().findPair(this.primer, this.primer2!);
      const success = (pair: biojs.PrimerPair): void => {
        const [fwdPrimer, revPrimer] = Array.from(pair.getPrimers(this.sequence.sequencePrimers()));
        // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
        // Error on `fwdPrimer` arg:
        // Argument of type 'SequencePrimer | SequencePrimer' is not assignable to parameter of type 'SequencePrimer'.
        //   Type 'SequencePrimer' is missing the following properties from type 'SequencePrimer': type, visible, hover, partnerSelected, and 17 more.ts(2345)
        this.sequence.get('selection').selectPair(fwdPrimer, revPrimer);
        this.sequence.get('selection').trigger('showLinks');
        this.model.close(false);
      };
      deferred = $.Deferred();
      if (existing != null) {
        notifications.success('Primer pair updated.');
        success(existing);
        return deferred.resolve();
      } else {
        const sequence = this.sequence;
        // BackboneCollection.Create returns the model (not a promise), so we create
        // promise and manually resolve/reject it. This entire codepath should eventually
        // be moved server side (T3603).
        sequence.primerPairs().create(
          {
            primer_fwd_id: this.primer.id,
            primer_rev_id: this.primer2!.id,
          },
          {
            wait: true,
            error(model: unknown, res: JQueryXHR): void {
              analytics.track('primer', 'save_primers_fail', {
                product: ANALYTICS_MODULE.MOLBIO,
                numPrimers: 2,
                error: notifications.getFriendlyError(res),
                sequence_id: sequence.get('id'),
              });
              deferred.reject();
            },
            success: (pair: biojs.PrimerPair) => {
              notifications.success('Primers successfully linked.');
              analytics.track('primer', 'linked_primers', {
                forward_primer: `${this.primer.id}-${this.primer.name()}`,
                reverse_primer: `${this.primer2!.id}-${this.primer2!.name()}`,
                sequence_id: this.sequence.get('id'),
              });
              this.trackSavePrimersSuccess(2, [
                this.primer.get('primer_api_id')!,
                this.primer2!.get('primer_api_id')!,
              ]);
              success(pair);
              deferred.resolve();
            },
          }
        );
        return deferred.promise();
      }
    };

    const savePrimer = (primer: BioJSSequencePrimer): JQueryPromise<void> => {
      this.model.saving(this.fieldIndex(primer), true);
      // preserve existing functionality by assuming that save will succeed
      return (primer.save(null, {fileUpdate: true}) as JQueryXHR)
        .fail((res) => {
          this.trackSavePrimersFailure(res, 2);
        })
        .done(() => {
          this.handleSavedPrimer(primer, false);
        });
    };

    const checkPrimer = (primer: BioJSSequencePrimer): JQueryPromise<unknown> => {
      deferred = $.Deferred();
      if (primer.has('primer_api_id')) {
        checkPrimerUtil.checkPrimer(
          primer.get('primer_api_id') ?? null,
          primer.bases(),
          () => deferred.resolve(),
          this.sequence.id,
          (res: Response) => {
            this.trackSavePrimersFailure(res, 2);
          }
        );
      } else {
        deferred.resolve();
      }
      return deferred.promise();
    };

    if (this.isNameTooLong(this.primer) || this.isNameTooLong(this.primer2!)) {
      return;
    }

    checkPrimer(this.primer)
      .then(() => checkPrimer(this.primer2!))
      .then(() => savePrimer(this.primer))
      .then(() => savePrimer(this.primer2!))
      .then(handlePair)
      .always(() => this.sequence.updateVersions());
  }

  renderPosition(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $position = this.$('.js-primer-position').eq(fieldIndex);
    $position.val(sequtil.modelToDisplay(primer.bindPosition(this._sequence), this._sequence.startIndex()));
  }

  renderStats(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $tm = this.$('.js-tm').eq(fieldIndex);
    const $gc = this.$('.js-gc').eq(fieldIndex);
    const $length = this.$('.js-length').eq(fieldIndex);

    const meltingTemp = primer.meltingTemperature();
    $tm.html(Number.isNaN(meltingTemp) ? '--' : `${meltingTemp.toFixed(1)}\u00B0C`);
    const gc = util.gc(primer.bindingBases(), primer.nucleotideType());
    $gc.text(Number.isNaN(gc) ? '--' : `${(gc * 100).toFixed(2)}%`);
    $length.html(`${primer.length()} bp`);

    const [minOverhang, maxOverhang] = Array.from(this.getMinMaxOverhang(primer));
    const $overhangSize = this.$('.js-overhang-size').eq(fieldIndex);
    $overhangSize.attr({min: minOverhang, max: maxOverhang});
    this.renderOverhangMessage(primer);
  }

  renderAssemblyFields(primer?: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const isAssembly = isAssemblyPrimer(this._sequence, primer);
    const isGibson = isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GIBSON);
    const isGoldenGate = isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GOLDEN_GATE);
    const classes = {
      '.overhang': !isAssembly,
      '.primer-position': !isAssembly,
      '.save-library': !isAssembly,
      '.binding': isAssembly,
      '.homology': isGibson,
      '.pre-recognition-site': isGoldenGate,
      '.post-recognition-site': isGoldenGate,
    };
    Object.entries(classes).forEach(([cls, visible]) => {
      this.$(cls).eq(fieldIndex).toggleClass('invisible', !visible);
      this.toggleGroup(cls);
    });
    this.$('.swatch-big')
      .eq(fieldIndex)
      .css('pointer-events', isAssembly ? 'none' : '');
    this.$('.js-binding').prop('disabled', !this.sequence.get('currentVersion'));
    this.$('.js-homology').prop('disabled', !this.sequence.get('currentVersion'));
  }

  renderAssemblyFieldValues(primer?: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $binding = this.$('.js-binding').eq(fieldIndex);
    $binding.val(primer.bindingBases().length || '');
    const $homology = this.$('.js-homology').eq(fieldIndex);
    $homology.val(primer.overhangLength() || '');

    if (isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GOLDEN_GATE)) {
      const {preRecognitionSite, postRecognitionSite} = this._getGoldenGatePrimerParts(primer);
      const $preRecognitionSite = this.$('.js-pre-recognition-site').eq(fieldIndex);
      if ($preRecognitionSite.val() !== preRecognitionSite) {
        $preRecognitionSite.val(preRecognitionSite);
      }
      const $postRecognitionSite = this.$('.js-post-recognition-site').eq(fieldIndex);
      if ($postRecognitionSite.val() !== preRecognitionSite) {
        $postRecognitionSite.val(postRecognitionSite);
      }
    }
  }

  renderNucleotideTypeRadioButton(): void {
    // if oligo is already created on the backend, then disable the nucleotide type radio buttons
    this.$el.find('.nucleotide-type-group input').prop('disabled', this.primer.referencesExistingOligo());
  }

  renderOverhangMessage(primer: BioJSSequencePrimer): void {
    if (primer == null) {
      ({primer} = this);
    }
    const fieldIndex = this.fieldIndex(primer);
    const $overhangSmall = this.$('.overhang-too-small-msg').eq(fieldIndex);
    const $overhangLarge = this.$('.overhang-too-large-msg').eq(fieldIndex);
    const $overhangSize = this.$('.js-overhang-size').eq(fieldIndex);
    const [minOverhang, maxOverhang] = Array.from(this.getMinMaxOverhang(primer));
    const val = $overhangSize.val().length > 0 ? parseInt($overhangSize.val(), 10) : 0;
    $overhangSmall.toggleClass('invisible', val >= minOverhang);
    $overhangLarge.toggleClass('invisible', val <= maxOverhang);
    this.toggleGroup('.overhang-too-small-msg');
    this.toggleGroup('.overhang-too-large-msg');
    $overhangSmall.find('.overhang-num').text(this.primer.get('overhang_length'));
    $overhangLarge.find('.overhang-num').text(this.primer.get('overhang_length'));
  }

  renderSaveButton(primer: BioJSSequencePrimer): void {
    const fieldIndex = this.fieldIndex(primer);
    const $save = this.$('.js-save').eq(fieldIndex);
    const saving = this.model.isSaving(fieldIndex);
    const saveDisabled =
      saving ||
      !primer.isValid() ||
      !this.sequence.get('currentVersion') ||
      this.isNameTooLong(primer) ||
      primer.fromSubPart();
    // @ts-expect-error we sometimes pass booleans to .attr()
    $save.toggleClass('disabled', saveDisabled).attr('disabled', saveDisabled);
  }

  renderSavePair(): void {
    const canSave =
      this.primer.isValid() &&
      this.primer2?.isValid() &&
      this.sequence.get('currentVersion') &&
      this.computeProductLength() > 0 &&
      !this.primer.fromSubPart() &&
      !this.primer2?.fromSubPart();
    const isSaving =
      this.model.isSaving(this.fieldIndex(this.primer)) || this.model.isSaving(this.fieldIndex(this.primer2));
    const saveDisabled =
      !canSave || isSaving || this.isNameTooLong(this.primer) || this.isNameTooLong(this.primer2!);
    this.$('.save-pair')
      .toggleClass('is-disabled', saveDisabled)
      // @ts-expect-error we sometimes pass booleans to .attr()
      .attr('disabled', saveDisabled);
  }

  renderMeltingTempDiff(): void {
    let diffStr;
    const fMeltingTemp = this.primer.meltingTemperature();
    const rMeltingTemp = this.primer2!.meltingTemperature();
    if (Number.isNaN(fMeltingTemp) || Number.isNaN(rMeltingTemp)) {
      diffStr = '--';
    } else {
      const diff = rMeltingTemp - fMeltingTemp;
      diffStr = diff.toFixed(2);
      if (diff > 0) {
        diffStr = `+${diffStr}`;
      }
      diffStr = `${diffStr}\u00B0C`;
    }
    this.$('.js-tm-diff').html(diffStr);
  }

  autoSecondaryCheck(): void {
    if (this.model.get('analyzeSecondary')) {
      _.defer(() => {
        this.$('.js-check-secondary').focus();
        this.$('.js-check-secondary').click();
        this.model.set({analyzeSecondary: false});
      });
    }
  }

  isNameTooLong(primer: BioJSSequencePrimer): boolean {
    return primer.get('name').length > MAX_PRIMER_NAME_LENGTH;
  }

  renderEnzymeSelector(): void {
    if (this.selectedEnzyme != null) {
      this.$('.js-reference').val(this.selectedEnzyme.get('seq'));
    }
    renderToDOM(
      <EnzymeSelector
        enzymes={this.enzymes}
        selectedEnzymeId={this.selectedEnzyme?.id ?? null}
        onSelect={(enzymeId: number) => {
          this.selectedEnzyme = this.enzymes.get(enzymeId);
          this.renderEnzymeSelector();
          this.$('.js-reference').select();
        }}
      />,
      this.$('.js-edit-primer-enzymes')[0]
    );
  }

  override render(): TabView {
    let isGibson;
    let isGoldenGate;
    let showEnzymes;
    let template;
    if (!this.sequence.get('currentVersion')) {
      // eslint-disable-next-line new-cap
      this.$el.html(PreviousVersionPrimerTemplate({}));
      return this;
    }

    if (this.sequence.isEmpty()) {
      this.model.removePrimers(this.primer, this.primer2);
      // eslint-disable-next-line new-cap
      this.$el.html(EmptySequencePrimerTemplate({}));
      return this;
    }

    const primer1Exists = this.model.get('primer').referencesExistingOligo();
    let primer2Exists = false;
    if (!this.model.has('primer2')) {
      template = EditPrimerTemplate;
      showEnzymes = !isAssemblyPrimer(this._sequence, this.model.get('primer'));
      isGibson = isAssemblyPrimer(this._sequence, this.model.get('primer'), ASSEMBLY_TYPE.GIBSON);
      isGoldenGate = isAssemblyPrimer(this._sequence, this.model.get('primer'), ASSEMBLY_TYPE.GOLDEN_GATE);
    } else {
      template = EditPrimerPairTemplate;
      primer2Exists = this.model.get('primer2')!.referencesExistingOligo();
      const checkBoth = (fn: (primer: BioJSSequencePrimer | null) => boolean): boolean =>
        fn(this.model.get('primer')) || fn(this.model.get('primer2'));
      showEnzymes = checkBoth((primer: BioJSSequencePrimer) => !isAssemblyPrimer(this._sequence, primer));
      isGibson = checkBoth((primer: BioJSSequencePrimer) =>
        isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GIBSON)
      );
      isGoldenGate = checkBoth((primer: BioJSSequencePrimer) =>
        isAssemblyPrimer(this._sequence, primer, ASSEMBLY_TYPE.GOLDEN_GATE)
      );
    }
    this.$el.html(
      template({
        showEnzymes,
        // eslint-disable-next-line new-cap
        assemblyPrimerHelp: AssemblyPrimerHelpTemplate({
          isGibson,
          isGoldenGate,
        }),
        isGoldenGate,
        primer1Exists,
        primer2Exists,
      })
    );
    this.$('.glyphicon-question-sign').tooltip({placement: 'bottom', container: 'body'});

    this.initializeFields(this.model.get('primer'), 0);
    if (this.model.get('primer2') != null) {
      this.initializeFields(this.model.get('primer2')!, 1);
    }

    this.stopListening(this.primer);
    if (this.primer2 != null) {
      this.stopListening(this.primer2);
    }
    // Only remove if it's now null. It's important to remove after stopListning and before new listeners
    // because the listener to close tab on primer remove should NOT get triggered.
    const primerToRemove = this.primer != null && this.model.get('primer') == null ? this.primer : null;
    const primerToRemove2 = this.primer2 != null && this.model.get('primer2') == null ? this.primer2 : null;
    this.model.removePrimers(primerToRemove, primerToRemove2);

    this.primer = this.model.get('primer');
    this.primer2 = this.model.get('primer2');

    this.$el.find('.nucleotide-type-group').removeClass('hidden');
    this.renderNucleotideTypeRadioButton();

    this.registerListeners();
    ({dna: this.dna, selection: this.selection} = this.model.get('sequence').attributes);

    this.renderStats(this.primer);
    this.renderSaveButton(this.primer);
    this.renderAssemblyFields(this.primer);
    this.renderAssemblyFieldValues(this.primer);
    this.$('.js-strand').prop('disabled', isAssemblyPrimer(this._sequence, this.primer));
    if (this.primer2 != null) {
      this.renderStats(this.primer2);
      this.renderSaveButton(this.primer2);
      this.renderAssemblyFields(this.primer2);
      this.renderAssemblyFieldValues(this.primer2);
      this.renderMeltingTempDiff();
      this.renderProductLength();
      this.renderSavePair();
    }

    if (showEnzymes) {
      this.renderEnzymeSelector();
    }
    this.renderLibraries();
    this.renderSecondary();
    this.renderSecondaryTemp();

    this.showBases(this.primer);
    this.colorPicker.attach(this.$('.swatch-big').eq(0), 'manual', '.edit-primer .swatch-big:eq(0)');
    this.colorPicker.update(this.primer);
    this.renderUsedPrimer(this.primer);
    this.renderBaseMsg(this.primer);
    this.findExistingName(this.primer);
    this.checkNameLength(this.primer);
    if (this.primer2 != null) {
      this.showBases(this.primer2);
      this.colorPicker2.attach(this.$('.swatch-big').eq(1), 'manual', '.edit-primer .swatch-big:eq(1)');
      this.colorPicker2.update(this.primer2);
      this.renderUsedPrimer(this.primer2);
      this.renderBaseMsg(this.primer2);
      this.findExistingName(this.primer2);
      this.checkNameLength(this.primer2);
    }

    this.model.addValidPrimers();
    this.autoSecondaryCheck();
    return this;
  }
}

TabView.initClass();

export {TabView, OptionsView};
